import React, { ComponentProps } from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';

import LoyaltyCoinIcon from '~/components/Icons/LoyaltyCoinIcon';

import { getLuxLoyaltyTierDisplayName } from '~/utils/luxLoyalty';

import InfiniteLogo from './InfiniteLogo';
import SocieteProgramNameLogo from './SocieteProgramNameLogo';

const SOCIETE_INLINE_LOGO_HEIGHT = 14;
const SOCIETE_INLINE_LOGO_WIDTH = 62;

const TierPill = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tier' && prop !== 'pillSize' && prop !== 'fullWidth',
})<{ tier: App.LuxLoyaltyTier; pillSize: 'small' | 'medium'; fullWidth?: boolean }>(({
  theme,
  tier,
  pillSize,
  fullWidth = false,
}) => {
  const isSmall = pillSize === 'small';
  const baseBorderRadius = typeof theme.shape.borderRadius === 'number' ? theme.shape.borderRadius : 4;

  return {
    alignItems: 'center',
    flexShrink: 0,
    maxWidth: '100%',
    borderRadius: baseBorderRadius * 4,
    fontSize: theme.typography.pxToRem(isSmall ? 12 : 13),
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: 1,
    background: theme.palette.loyaltyTiers[tier].gradient,
    color: theme.palette.loyaltyTiers[tier].contrastText,
    ...(fullWidth
      ? {
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          alignSelf: 'stretch',
        }
      : {
          display: 'inline-flex',
          width: 'fit-content',
          alignSelf: 'flex-start',
        }),
    ...(tier === 'infinite'
      ? {
          minHeight: isSmall ? 24 : 32,
          padding: theme.spacing(isSmall ? 0.5 : 0.75, isSmall ? 1 : 1.5),
          ...(fullWidth && {
            padding: theme.spacing(isSmall ? 0.75 : 1, isSmall ? 1 : 1.5),
          }),
        }
      : {
          height: isSmall ? 24 : 32,
          padding: theme.spacing(0.75, isSmall ? 1 : 1.5),
          gap: theme.spacing(0.75),
        }),
  };
});

type InheritedPillProps = Omit<ComponentProps<typeof Box>, 'children'>;

interface Props extends InheritedPillProps {
  luxLoyaltyAccount: App.LuxLoyaltyAccount;
  size?: 'small' | 'medium';
  /** Full-width banner with centred content — used on the Account tab. */
  fullWidth?: boolean;
  /** When set, the pill is interactive and shows a navigate indicator. */
  onClick?: () => void;
}

function InfiniteAccountChipLabel({
  accountNumber,
  conciergeRepName,
  fullWidth = false,
}: {
  accountNumber?: number;
  conciergeRepName: string;
  fullWidth?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 1, sm: 0.75 },
        flexWrap: 'wrap',
        color: 'inherit',
        lineHeight: 1.45,
        textAlign: fullWidth ? 'center' : undefined,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ lineHeight: 1 }}>
        <Box component="span" sx={{ lineHeight: 0, display: 'flex', alignItems: 'center' }}>
          <InfiniteLogo width={91} height={14} aria-hidden style={{ display: 'block' }} />
        </Box>
        <Box component="span">
          Member <b>#{accountNumber}</b>
        </Box>
      </Stack>
      <Box component="span" sx={{ color: 'inherit' }}>
        <Box component="span" aria-hidden sx={{ display: { xs: 'none', sm: 'inline' }, mx: 0.75 }}>
          |
        </Box>
        Concierge Representative: {conciergeRepName}
      </Box>
    </Box>
  );
}

function TierAccountChipLabel({
  tier,
  accountNumber,
  logoHeight = SOCIETE_INLINE_LOGO_HEIGHT,
  coinSize,
}: {
  tier: App.LuxLoyaltyTier;
  accountNumber?: number;
  logoHeight?: number;
  coinSize: number;
}) {
  const logoWidth = (SOCIETE_INLINE_LOGO_WIDTH / SOCIETE_INLINE_LOGO_HEIGHT) * logoHeight;

  return (
    <>
      <Box component="span" aria-hidden sx={{ lineHeight: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <LoyaltyCoinIcon tier={tier} size={coinSize} />
      </Box>
      <Box component="span" aria-hidden sx={{ lineHeight: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <SocieteProgramNameLogo
          variant="inline"
          width={logoWidth}
          height={logoHeight}
          aria-hidden
          style={{ display: 'block' }}
        />
      </Box>
      <Box component="span" sx={{ lineHeight: 1 }}>
        <b>{getLuxLoyaltyTierDisplayName(tier)}</b> Member <b>#{accountNumber}</b>
      </Box>
    </>
  );
}

function LuxLoyaltyAccountChip({
  luxLoyaltyAccount,
  sx,
  size = 'medium',
  fullWidth = false,
  onClick,
  ...pillProps
}: Props) {
  const isInfinite = luxLoyaltyAccount.tier === 'infinite';
  const isClickable = !!onClick;
  const logoHeight = size === 'small' ? 12 : SOCIETE_INLINE_LOGO_HEIGHT;
  const coinSize = size === 'small' ? 16 : 20;
  const conciergeRepName = luxLoyaltyAccount.customerServiceRep
    ? `${luxLoyaltyAccount.customerServiceRep.firstName} ${luxLoyaltyAccount.customerServiceRep.lastName}`
    : 'Not assigned';

  const chipLabel = isInfinite ? (
    <InfiniteAccountChipLabel
      accountNumber={luxLoyaltyAccount.accountNumber}
      conciergeRepName={conciergeRepName}
      fullWidth={fullWidth}
    />
  ) : (
    <TierAccountChipLabel
      tier={luxLoyaltyAccount.tier}
      accountNumber={luxLoyaltyAccount.accountNumber}
      logoHeight={logoHeight}
      coinSize={coinSize}
    />
  );

  const clickableSx = isClickable
    ? {
        cursor: 'pointer',
        transition: (theme: import('@mui/material/styles').Theme) =>
          theme.transitions.create('filter', { duration: theme.transitions.duration.shorter }),
        '&:hover': {
          filter: 'brightness(1.08)',
        },
      }
    : undefined;

  const pill = (
    <TierPill
      tier={luxLoyaltyAccount.tier}
      pillSize={size}
      fullWidth={fullWidth}
      sx={[clickableSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      {...(isClickable ? {} : pillProps)}
    >
      {isClickable ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            width: fullWidth ? '100%' : undefined,
            justifyContent: fullWidth ? 'center' : undefined,
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: isInfinite ? undefined : 0.75,
            }}
          >
            {chipLabel}
          </Box>
          <ChevronRightIcon
            aria-hidden
            sx={{
              fontSize: size === 'small' ? 16 : 18,
              flexShrink: 0,
              opacity: 0.9,
            }}
          />
        </Stack>
      ) : (
        chipLabel
      )}
    </TierPill>
  );

  if (isClickable) {
    return (
      <Box
        component="button"
        type="button"
        onClick={onClick}
        aria-label="Open Société profile"
        sx={{
          border: 0,
          padding: 0,
          margin: 0,
          background: 'none',
          font: 'inherit',
          color: 'inherit',
          cursor: 'pointer',
          display: fullWidth ? 'flex' : 'inline-flex',
          width: fullWidth ? '100%' : 'fit-content',
          textAlign: 'inherit',
          '&:focus-visible': {
            outline: '2px solid currentColor',
            outlineOffset: 2,
            borderRadius: 1,
          },
        }}
      >
        {pill}
      </Box>
    );
  }

  return pill;
}

export default LuxLoyaltyAccountChip;
